/* eslint-disable react/no-unescaped-entities */
import { FC } from "react";
import { Link } from "react-router-dom";
import { BreadcrumbSchema, Seo } from "@shared/components";
import { CONTACTS, PRICING_FACTS } from "@shared/config/seo-schemas";

export const OfferPage: FC = () => {
  return (
    <>
      <Seo
        title="Публичная оферта | Polotno"
        description="Публичная оферта сервиса Polotno — договор на оказание услуг по транскрибации аудио- и видеофайлов."
        path="/offer"
      />
      <BreadcrumbSchema items={[{ name: "Главная", url: "/" }, { name: "Публичная оферта" }]} />
      <div className="container mx-auto py-12 px-4 md:px-6 lg:px-8">
        <h1 className="text-4xl font-bold mb-6 text-center">Публичная оферта</h1>
        <p className="text-muted-foreground text-center mb-10">Последнее обновление: 14.09.2026</p>

        <div className="max-w-3xl mx-auto prose prose-lg dark:prose-invert">
          <p className="mb-6">
            Настоящий документ является официальным предложением (публичной офертой) самозанятого Постика Максима
            Феликсовича (ИНН 311404938543), именуемого в дальнейшем «Исполнитель», в адрес любых физических и/или
            юридических лиц (далее — «Пользователи») заключить Договор на оказание услуг по предоставлению доступа к
            сервису «Полотно» (далее — «Договор») на условиях, изложенных в настоящей Оферте.
          </p>
          <p className="mb-6">
            Акцептом (принятием) настоящей Оферты является регистрация Пользователя в Сервисе и/или оплата услуг
            Исполнителя. С момента Акцепта Оферты Договор считается заключенным между Сторонами.
          </p>

          <h2 className="text-2xl font-semibold mb-4">1. Термины и определения</h2>
          <ul className="list-disc pl-6 mb-6">
            <li>
              <strong>Сервис</strong> — программный продукт «Полотно», функционирующий в сети Интернет по адресу{" "}
              <a
                href="https://polotno.tech"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                https://polotno.tech
              </a>
              , а также Telegram-бот «Полотно», расположенный по адресу{" "}
              <a
                href="https://t.me/PolontoTranscribe_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                https://t.me/PolontoTranscribe_bot
              </a>
              , предназначенный для транскрибации аудио- и видеофайлов с использованием технологий искусственного
              интеллекта.
            </li>
            <li>
              <strong>Исполнитель</strong> — самозанятый Постика Максим Феликсович (ИНН 311404938543).
            </li>
            <li>
              <strong>Пользователь</strong> — физическое или юридическое лицо, осуществившее Акцепт настоящей Оферты.
            </li>
            <li>
              <strong>Тарифы</strong> — условия о стоимости, сроках и объеме услуг, размещенные на официальном сайте
              Исполнителя.
            </li>
            <li>
              <strong>Подписка</strong> — выбранный и оплаченный Пользователем Тариф для использования функционала
              Сервиса.
            </li>
            <li>
              <strong>Пробный период</strong> — платный Тариф с ограниченным сроком действия и объемом минут,
              предназначенный для ознакомления с Сервисом, по окончании которого Подписка автоматически продлевается на
              Тариф «Базовый» в порядке, предусмотренном п. 7.8 Оферты.
            </li>
            <li>
              <strong>Баланс</strong> — счетчик в Сервисе, фиксирующий авансовые платежи Пользователя и использованные
              минуты транскрибации.
            </li>
            <li>
              <strong>Учетная запись</strong> — личный кабинет Пользователя в Сервисе, предназначенный для доступа к
              функционалу.
            </li>
            <li>
              <strong>Персональные данные</strong> — сведения, относящиеся к определенному физическому лицу,
              обрабатываемые в соответствии с Политикой конфиденциальности.
            </li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">2. Предмет Договора</h2>
          <p className="mb-4">
            2.1. Исполнитель обязуется предоставить Пользователю удаленный доступ к Сервису через сеть Интернет, а
            Пользователь обязуется выплачивать вознаграждение за предоставление такого доступа в соответствии с
            выбранным Тарифом.
          </p>
          <p className="mb-4">
            2.2. Сервис предназначен для транскрибации (преобразования в текст) аудио- и видеофайлов с использованием
            технологий искусственного интеллекта.
          </p>
          <p className="mb-4">
            2.3. С целью ознакомления с возможностями Сервиса Пользователь может оформить Пробный период стоимостью{" "}
            {PRICING_FACTS.trial.price} ₽, который включает {PRICING_FACTS.trial.minutes} минут транскрибации и
            действует {PRICING_FACTS.trial.days} календарных дней с момента оплаты. Пробный период доступен однократно и
            только Пользователям, ранее не совершавшим покупок в Сервисе. Пробный период является платной Подпиской с
            автоматическим продлением на условиях п. 7.8 Оферты.
          </p>
          <p className="mb-6">
            2.4. Стоимость предоставления доступа к Сервису указывается в Тарифах на официальном сайте Исполнителя.
            Тарифы являются неотъемлемой частью Договора.
          </p>

          <h2 className="text-2xl font-semibold mb-4">3. Порядок заключения Договора</h2>
          <p className="mb-4">3.1. Акцептом Оферты считается совершение Пользователем любого из следующих действий:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>регистрация в Сервисе;</li>
            <li>оплата Подписки в порядке и на условиях, указанных в Договоре.</li>
          </ul>
          <p className="mb-4">3.2. При совершении Акцепта считается, что:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>Пользователь ознакомился и согласился с условиями Оферты;</li>
            <li>Договор заключен и действителен в электронном виде;</li>
            <li>письменная форма сделки соблюдена.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">4. Условия предоставления доступа к Сервису</h2>
          <p className="mb-4">
            4.1. Условием получения доступа к платным функциям Сервиса является оплата Подписки в соответствии с
            выбранным Тарифом и соблюдение Пользователем правил использования Сервиса.
          </p>
          <p className="mb-4">
            4.2. Пользователь самостоятельно несет ответственность за достоверность вносимых данных при регистрации.
          </p>
          <p className="mb-4">
            4.3. Пользователь несет ответственность за сохранность данных для авторизации в Сервисе.
          </p>
          <p className="mb-4">4.4. При использовании Сервиса Пользователю запрещается:</p>
          <ul className="list-disc pl-6 mb-6">
            <li>получать несанкционированный доступ к ресурсам Исполнителя и данным других Пользователей;</li>
            <li>использовать Сервис для незаконных целей;</li>
            <li>передавать доступ к своей Учетной записи третьим лицам без разрешения Исполнителя.</li>
          </ul>

          <h2 className="text-2xl font-semibold mb-4">5. Регистрация и использование Сервиса</h2>
          <p className="mb-4">
            5.1. Для получения доступа к Сервису Пользователю необходимо пройти регистрацию на официальном сайте
            Исполнителя.
          </p>
          <p className="mb-4">5.2. Регистрируя Учетную запись, Пользователь подтверждает, что:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>достиг возраста 13 лет;</li>
            <li>ознакомился с условиями Оферты и полностью их принимает;</li>
            <li>имеет техническую возможность использовать Сервис;</li>
            <li>дает согласие на обработку своих Персональных данных.</li>
          </ul>
          <p className="mb-6">
            5.3. Доступ к Сервису предоставляется на условиях «1 аккаунт = 1 человек». Передача Учетной записи другому
            лицу является нарушением условий Договора.
          </p>

          <h2 className="text-2xl font-semibold mb-4">6. Порядок использования Сервиса</h2>
          <p className="mb-4">
            6.1. Срок Подписки составляет один календарный месяц при ежемесячной оплате и один календарный год при
            оплате за год и начинает исчисляться с момента оплаты выбранного Тарифа. Если в месяце окончания срока нет
            числа, соответствующего дате оплаты, срок продлевается на недостающее количество дней за счёт следующего
            месяца. Срок может быть продлен на период повторных попыток списания в порядке, предусмотренном п. 7.7
            Оферты.
          </p>
          <p className="mb-4">
            6.2. После получения доступа к Сервису его возможности используются Пользователем самостоятельно.
          </p>
          <p className="mb-4">
            6.3. Пользователь может обратиться за технической поддержкой через{" "}
            <a
              href="https://t.me/Polotno_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Telegram бот поддержки
            </a>{" "}
            или по email{" "}
            <a href="mailto:hello@polotno.tech" className="text-blue-500 hover:underline">
              hello@polotno.tech
            </a>
            .
          </p>
          <p className="mb-4">
            6.4. Обязательства Исполнителя считаются исполненными с момента предоставления возможности доступа к Сервису
            на условиях оплаченной Подписки, с учетом права Пользователя на возврат денежных средств в порядке,
            предусмотренном разделом 8 Оферты.
          </p>
          <p className="mb-6">
            6.5. По истечении периода действия Подписки Пользователю ограничивается доступ к платным возможностям
            Сервиса, с учетом п. 7.7 Оферты.
          </p>
          <p className="mb-6">
            6.6. При оплате Подписки за год объём минут транскрибации, предусмотренный Тарифом, предоставляется
            ежемесячно: каждый месяц в число, соответствующее дате оплаты (а если в месяце нет такого числа — с
            переносом недостающих дней на следующий месяц), остаток минут по Тарифу восстанавливается до месячного
            объёма. Неиспользованные минуты предыдущего месяца на следующий месяц не переносятся. Дополнительно
            приобретённые минуты сохраняются.
          </p>

          <h2 className="text-2xl font-semibold mb-4">7. Стоимость услуг и порядок оплаты</h2>
          <p className="mb-4">
            7.1. Стоимость Подписки определяется в соответствии с действующими Тарифами Исполнителя.
          </p>
          <p className="mb-4">
            7.2. Оплата Подписки осуществляется на условиях 100% предварительной оплаты любым из способов, предложенных
            на сайте.
          </p>
          <p className="mb-4">
            7.3. Стоимость Подписки не подлежит обложению НДС в соответствии с налоговым режимом «Налог на
            профессиональный доход».
          </p>
          <p className="mb-4">
            7.4. Обязанность Пользователя по оплате считается исполненной в момент зачисления денежных средств на счет
            Исполнителя.
          </p>
          <p className="mb-4">
            7.5. Исполнитель вправе изменять Тарифы в любое время. Новые Тарифы вступают в силу с момента их публикации
            на сайте и не распространяются на уже оплаченные Подписки.
          </p>
          <p className="mb-6">
            7.6. Пользователь подтверждает свое согласие, что после первой оплаты Подписки по выбранному Тарифу, в том
            числе Пробного периода, списание денежных средств за продление Подписки на последующий период (месяц или год
            — в зависимости от выбранного периода оплаты) будет происходить автоматически с банковской карты
            Пользователя, с которой был осуществлен первоначальный платеж. Пользователь может в любой момент отменить
            автоматический платеж в настройках Учетной записи или обратившись в службу поддержки.
          </p>
          <p className="mb-6">
            7.7. Если автоматическое списание за продление Подписки не прошло, Исполнитель вправе повторить попытку
            списания с той же банковской карты до трех раз в течение 7 календарных дней с даты окончания оплаченного
            периода. На этот срок доступ Пользователя к платным возможностям Сервиса сохраняется. При успешном списании
            новый период Подписки исчисляется с даты окончания предыдущего оплаченного периода. Если ни одна из попыток
            не была успешной, автоматическое продление прекращается, доступ к платным возможностям Сервиса
            ограничивается, и дальнейшие списания не производятся. Пользователь может в любой момент отменить
            автоматический платеж в настройках Учетной записи.
          </p>
          <p className="mb-4">
            7.8. При оплате Пробного периода банковская карта Пользователя привязывается к Учетной записи для
            последующих автоматических списаний. По окончании Пробного периода Подписка автоматически переходит на Тариф
            «Базовый», и с привязанной карты списывается {PRICING_FACTS.basic.price} ₽ за один календарный месяц доступа
            ({PRICING_FACTS.basic.minutes} минут транскрибации). Стоимость Тарифа «Базовый» фиксируется на момент оплаты
            Пробного периода. Последующие продления производятся ежемесячно в порядке, предусмотренном п. 7.6 и 7.7
            Оферты.
          </p>
          <p className="mb-6">
            7.9. Чтобы автоматический переход на Тариф «Базовый» не произошел, Пользователь должен отменить Подписку в
            разделе «Подписка» Учетной записи или через службу поддержки до окончания Пробного периода. В этом случае
            доступ к Пробному периоду сохраняется до его окончания, после чего списания не производятся.
          </p>
          <p className="mb-4">
            7.10. Пользователь вправе перейти на Тариф с большим объёмом услуг и (или) на оплату за год до окончания
            текущего оплаченного периода при условии, что ни Тариф, ни период оплаты не уменьшаются; в остальных случаях
            применяется п. 7.11 Оферты. Переход происходит в момент оплаты. В оплату нового Тарифа засчитывается
            стоимость неиспользованной части текущего оплаченного периода. Для каждого месяца периода она определяется
            пропорционально меньшему из двух значений: доли оставшихся дней месяца или доли неизрасходованных минут,
            включённых в Тариф на этот месяц. Месяцы годового периода, минуты за которые ещё не начислены, засчитываются
            полностью. Итоговая сумма к оплате отображается Пользователю до оплаты. Новый период Подписки исчисляется с
            даты оплаты.
          </p>
          <p className="mb-4">
            7.11. Переход на Тариф с меньшим объёмом услуг или с оплаты за год на ежемесячную оплату происходит по
            окончании текущего оплаченного периода. До этой даты Пользователь пользуется текущим Тарифом в полном
            объёме. В дату окончания периода с привязанной карты списывается стоимость нового Тарифа. Денежные средства
            за текущий период при этом не возвращаются и не пересчитываются. Пользователь может отменить запланированный
            переход в разделе «Подписка» Учетной записи до даты его вступления в силу.
          </p>
          <p className="mb-6">
            7.12. При оплате Подписки за год Исполнитель не позднее чем за 7 календарных дней до даты автоматического
            продления Подписки на следующий год направляет Пользователю уведомление о предстоящем списании на адрес
            электронной почты, указанный в Учетной записи, и (при наличии) в Telegram.
          </p>

          <h2 className="text-2xl font-semibold mb-4">8. Условия возврата денежных средств</h2>
          <p className="mb-4">
            8.1. Пользователь вправе в любой момент отказаться от Договора и потребовать возврата денежных средств за
            оплаченную Подписку или пакет минут. Возврату подлежит оплаченная сумма за вычетом стоимости фактически
            оказанных услуг. Стоимость оказанных услуг рассчитывается пропорционально использованным минутам: стоимость
            Подписки или пакета минут делится на количество включенных в них минут и умножается на количество минут,
            использованных Пользователем в текущем оплаченном периоде.
          </p>
          <p className="mb-4">8.2. Помимо п. 8.1, возврат денежных средств осуществляется в следующих случаях:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>
              технические неполадки в Сервисе, препятствующие использованию более 24 часов — возврат пропорционально
              периоду недоступности;
            </li>
            <li>
              удаление или блокировка Учетной записи по инициативе Исполнителя без нарушений со стороны Пользователя.
            </li>
          </ul>
          <p className="mb-4">
            8.3. Для получения возврата Пользователь должен обратиться через{" "}
            <a
              href="https://t.me/Polotno_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Telegram бот поддержки
            </a>{" "}
            или по email{" "}
            <a href="mailto:hello@polotno.tech" className="text-blue-500 hover:underline">
              hello@polotno.tech
            </a>{" "}
            с указанием email Учетной записи и даты списания.
          </p>
          <p className="mb-6">
            8.4. Возврат производится на банковскую карту, с которой была произведена оплата, в течение 10 рабочих дней
            с момента принятия решения о возврате. Срок зачисления денежных средств на карту зависит от банка
            Пользователя. После возврата доступ к оплаченной Подписке или пакету минут прекращается, а автоматическое
            продление отключается.
          </p>

          <h2 className="text-2xl font-semibold mb-4">9. Ответственность сторон</h2>
          <p className="mb-4">
            9.1. За невыполнение обязательств по Договору Стороны несут ответственность в соответствии с
            законодательством РФ.
          </p>
          <p className="mb-4">9.2. Исполнитель не несет ответственности за:</p>
          <ul className="list-  pl-6 mb-4">
            <li>неисправности каналов связи или проблемы с доступом в Интернет;</li>
            <li>утрату Пользователем доступа к Учетной записи;</li>
            <li>действия третьих лиц (DDoS-атаки, хакерские атаки);</li>
            <li>качество транскрибации при низком качестве исходного аудио/видео файла;</li>
            <li>обстоятельства непреодолимой силы.</li>
          </ul>
          <p className="mb-6">
            9.3. Ответственность Исполнителя ограничивается суммой оплаченной Пользователем Подписки.
          </p>

          <h2 className="text-2xl font-semibold mb-4">10. Обстоятельства непреодолимой силы</h2>
          <p className="mb-4">
            10.1. Стороны освобождаются от ответственности за неисполнение обязательств, если это явилось следствием
            обстоятельств непреодолимой силы (стихийные бедствия, военные действия, решения государственных органов).
          </p>
          <p className="mb-6">
            10.2. При наступлении таких обстоятельств срок исполнения обязательств продлевается на период их действия.
          </p>

          <h2 className="text-2xl font-semibold mb-4">11. Персональные данные</h2>
          <p className="mb-6">
            При использовании Сервиса Исполнитель собирает и обрабатывает Персональные данные Пользователя в
            соответствии с{" "}
            <Link to="/privacy" className="text-blue-500 hover:underline">
              Политикой конфиденциальности
            </Link>
            .
          </p>

          <h2 className="text-2xl font-semibold mb-4">12. Порядок разрешения споров</h2>
          <p className="mb-4">12.1. Споры разрешаются путем переговоров. Претензионный порядок обязателен.</p>
          <p className="mb-4">
            12.2. Претензия направляется через{" "}
            <a
              href="https://t.me/Polotno_support_bot"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              Telegram бот поддержки
            </a>{" "}
            или на email{" "}
            <a href="mailto:hello@polotno.tech" className="text-blue-500 hover:underline">
              hello@polotno.tech
            </a>{" "}
            и рассматривается в течение 10 календарных дней.
          </p>
          <p className="mb-6">
            12.3. При невозможности урегулирования спор передается в суд по месту нахождения Исполнителя.
          </p>

          <h2 className="text-2xl font-semibold mb-4">13. Изменение условий Договора</h2>
          <p className="mb-4">
            13.1. Исполнитель вправе в одностороннем порядке изменять условия Оферты. Изменения вступают в силу с
            момента публикации на сайте.
          </p>
          <p className="mb-6">
            13.2. Продолжение использования Сервиса после публикации изменений означает согласие Пользователя с новыми
            условиями.
          </p>

          <h2 className="text-2xl font-semibold mb-4">14. Заключительные положения</h2>
          <p className="mb-4">14.1. Договор регулируется законодательством Российской Федерации.</p>
          <p className="mb-4">
            14.2. Договор вступает в силу с момента Акцепта и действует до полного исполнения обязательств Сторонами.
          </p>
          <p className="mb-6">
            14.3. Актуальная версия Оферты размещена на сайте{" "}
            <a
              href="https://polotno.tech/offer"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              polotno.tech/offer
            </a>
            .
          </p>

          <h2 className="text-2xl font-semibold mb-4">15. Реквизиты Исполнителя</h2>
          <div className="bg-muted/50 p-4 rounded-lg mb-6">
            <p className="mb-2">
              <strong>Самозанятый:</strong> Постика Максим Феликсович
            </p>
            <p className="mb-2">
              <strong>ИНН:</strong> 311404938543
            </p>
            <p className="mb-2">
              <strong>Email:</strong>{" "}
              <a href={`mailto:${CONTACTS.email}`} className="text-blue-500 hover:underline">
                {CONTACTS.email}
              </a>
            </p>
            <p>
              <strong>Поддержка:</strong>{" "}
              <a
                href="https://t.me/Polotno_support_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:underline"
              >
                Telegram бот
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};
